<template>
    <IPNav
        ref="navBarRef"
        class="text-[14px]"
        :navModuleType="NAV_MENU_TYPE.NEW_TASKS"
        :ipNavService="ipNavService"
    />
    <UserPreferencesDialog
        v-model:visible="preferencesDialogVisible"
        :initialTaskType="AgentType.PATENT_DRAFT"
    />
</template>

<script setup lang="ts">
import {
    IPNav,
    NAV_MENU_TYPE,
    USER_PREFERENCES_MENU_TYPE,
    createIpNavService,
} from '@eureka/shared/src/components/ip-nav';
import { headerService } from '@/services/header';
import { useRouter } from 'vue-router';
import { ref, watch } from 'vue';
import psHttp from '@patsnap/common-ps-http';
import { useNavBar } from '@/composables/useNavBar';
import { useLoginSdk } from '@/composables/useLoginSdk';
import { usePlan } from '@eureka/shared/src/composables/useAuth';
import { DRAFTING_URI, EUREKA_SERVICE_URI } from '@/constants/base-url';
import { useLastProjectInfo } from '@/composables/useLastProjectInfo';
import { useAgentTabs } from '@/features/drafting/composables/use-agent-tabs';
import { AgentType } from '@/features/drafting/constants/enum';
import { getSiteRegion, SiteRegion } from '@eureka/shared/src/utils/site-region';
import { createUpgradeTrackingContext, goToUpgrade } from '@/utils/upgrade-tracking';
import UserPreferencesDialog from '@/features/patent-editor/components/UserPreferencesDialog.vue';

interface NavNavigateContext {
    menu: { type: string };
    preventDefault: () => void;
}

const { value: plan } = usePlan();
const { currentReturnAgentType } = useLastProjectInfo();
const { activeTab } = useAgentTabs();
const preferencesDialogVisible = ref(false);

// 侧边栏在所有页面都显示，按可靠性逐级推导“当前 agent”：
// 1) 当前打开的项目（项目页 URL 无 agent= 参数，靠 work_type）
// 2) 首页/agent 输入页选中的 tab（对应 URL ?agent=xxx）
const VALID_AGENT_TYPES = new Set<string>(Object.values(AgentType));
function resolveCurrentAgentType(): AgentType | null {
    const fromProject = currentReturnAgentType.value;
    if (typeof fromProject === 'string' && VALID_AGENT_TYPES.has(fromProject)) {
        return fromProject as AgentType;
    }
    return activeTab.value ?? null;
}

const { openLoginDialog } = useLoginSdk();

function openLogin() {
    openLoginDialog(false);
}

const router = useRouter();
const { isCollapsed, setCollapsed } = useNavBar();
// 积分入口统一进本地 /credits（hash 路由），由路由守卫按"个人 Max"决定是否重定向到 f-eureka 通用页
const creditsPageUrl = '#/credits';

// 获取所有路由的 name 集合
const routeNames = new Set(
    router.getRoutes()
        .map(route => route.name)
        .filter(name => name),
);

// 封装 psHttp 为符合 IAxiosClient 接口的对象
const httpClient = {
    get: (config: { url: string; params?: any; [key: string]: any }) =>
        psHttp({ ...config, method: 'GET' }).then(res => res.data),
    post: (config: { url: string; data?: any; [key: string]: any }) =>
        psHttp({ ...config, method: 'POST' }).then(res => res.data),
    delete: (config: { url: string; [key: string]: any }) =>
        psHttp({ ...config, method: 'DELETE' }).then(res => res.data),
};

const ipNavService = createIpNavService({
    headerService: headerService as any,
    httpClient,
    apiBaseUrl: EUREKA_SERVICE_URI,
    creditsConfig: {
        enabled: true,
        creditsPageUrl,
        apiBaseUrl: EUREKA_SERVICE_URI,
        draftingApiBaseUrl: DRAFTING_URI,
    },
    responsive: false,
    isActive: (context) => {
        const path = context.routePath;

        // 从 router 配置中查找匹配的路由,返回其 name
        const matchedRoute = router.getRoutes().find(route => {
            // 精确匹配
            if (route.path === path) {
                return true;
            }
            // 路径包含匹配(用于子路由)
            if (path.startsWith(route.path) && route.path !== '/') {
                return true;
            }
            return false;
        });

        return matchedRoute?.name as string || null;
    },
    onNavigate: (context: NavNavigateContext) => {
        if (context.menu.type === USER_PREFERENCES_MENU_TYPE) {
            context.preventDefault();
            preferencesDialogVisible.value = true;
            return;
        }

        if (context.menu.type === NAV_MENU_TYPE.NEW_TASKS as string) {
            if (typeof currentReturnAgentType.value === 'string' && currentReturnAgentType.value) {
                context.preventDefault();
                router.push({
                    path: '/',
                    query: { agent: currentReturnAgentType.value },
                });
            } else {
                context.preventDefault();
                location.href = '/';
            }
            return;
        }

        // 如果菜单的 type 属于项目路由名称,则阻止默认跳转,使用项目路由
        if (context.menu?.type && routeNames.has(context.menu.type)) {
            context.preventDefault();
            router.push({ name: context.menu.type });
        }
    },
    isShowUpgrade() {
        const isCn = getSiteRegion() === SiteRegion.CN;
        return !!(plan.value?.isFreemium && plan.value?.isPersonal) && !isCn;
    },
    onLogin: () => {
        openLogin();
    },
    onUpgrade: () => {
        goToUpgrade(router, createUpgradeTrackingContext({
            trigger_source: 'side_nav',
            agent_type: resolveCurrentAgentType(),
        }));
    },
});

watch(preferencesDialogVisible, (visible) => {
    if (!visible) {
        ipNavService.isActiveFromRoute(ipNavService.getMenus(), router.currentRoute.value);
    }
});

watch(isCollapsed, (collapsed) => {
    const opened = !collapsed;
    if (ipNavService.opened.value !== opened) {
        ipNavService.toggleSidebar(opened);
    }
}, { immediate: true });

watch(ipNavService.opened, (opened) => {
    const collapsed = !opened;
    if (isCollapsed.value !== collapsed) {
        setCollapsed(collapsed);
    }
});
</script>
